import { TypedDocumentNode, useSuspenseQuery } from '@apollo/client';

import { NotFoundError } from '@eluve/eluve-errors';

type NullableKeysOf<T> = {
  [K in keyof T]: null extends T[K] ? K : never;
}[keyof T];

type NarrowFieldNonNull<TData, K extends keyof TData> = Omit<TData, K> & {
  [P in K]: NonNullable<TData[P]>;
};

export interface EntityByIdOptions<TEntityKey> {
  entityKey: TEntityKey;
  entityType: string;
}

/**
 * Throws {@link NotFoundError} when the entity field on the query result is
 * null, so a nearby `NotFoundBoundary` can render a Not Found treatment
 * instead of the dangling-reference crash that a downstream fragment read
 * would produce.
 */
export function useEntityByIdSuspenseQuery<
  TData,
  TVariables extends Record<string, unknown>,
  TEntityKey extends NullableKeysOf<TData>,
>(
  query: TypedDocumentNode<TData, TVariables>,
  variables: TVariables,
  options: EntityByIdOptions<TEntityKey>,
): NarrowFieldNonNull<TData, TEntityKey> {
  const { data } = useSuspenseQuery(query, { variables });
  const entity = (data as TData)[options.entityKey];
  if (entity == null) {
    throw new NotFoundError(`${options.entityType} not found`);
  }
  return data as NarrowFieldNonNull<TData, TEntityKey>;
}
